import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CreditCard, Copy, CheckCircle, Loader2, ExternalLink, RefreshCw, Unlink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  isPaymentBotConfigured,
  getUserPaymentBotConnection,
  generatePaymentBotConnectionCode,
  disconnectPaymentBotUser
} from '@/services/telegram/paymentNotificationService';
import { supabase } from '@/integrations/supabase/client';

interface PaymentBotConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PaymentBotConnectDialog: React.FC<PaymentBotConnectDialogProps> = ({
  open,
  onOpenChange
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userConnection, setUserConnection] = useState<any>(null);
  const [connectionCode, setConnectionCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [botUsername, setBotUsername] = useState<string | null>(null);

  const checkStatus = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const configured = await isPaymentBotConfigured();
      setIsConfigured(configured);

      if (configured) {
        const conn = await getUserPaymentBotConnection(user.id);
        setUserConnection(conn);

        // Get bot info
        try {
          const { data } = await supabase.functions.invoke('telegram-payment-bot', {
            body: { action: 'test-connection' }
          });
          if (data?.bot_info?.username) {
            setBotUsername(data.bot_info.username);
          }
        } catch (e) {
          console.error('Error getting bot info:', e);
        }
      }
    } catch (error) {
      console.error('Error checking payment bot status:', error);
      setIsConfigured(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      checkStatus();
      setConnectionCode(null);
      setExpiresAt(null);
    }
  }, [open, user?.id]);

  const handleGenerateCode = async () => {
    if (!user?.id) return;
    
    setIsGeneratingCode(true);
    try {
      const result = await generatePaymentBotConnectionCode(user.id);
      if (result) {
        setConnectionCode(result.code);
        setExpiresAt(result.expires_at);
        toast({
          title: "Code Generated",
          description: "Use this code to connect your Telegram account."
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to generate connection code.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate connection code.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleCopyCode = () => {
    if (connectionCode) {
      navigator.clipboard.writeText(`/connect ${connectionCode}`);
      toast({
        title: "Copied!",
        description: "Command copied to clipboard."
      });
    }
  };

  const handleDisconnect = async () => {
    if (!user?.id) return;
    
    setIsDisconnecting(true);
    try {
      const success = await disconnectPaymentBotUser(user.id);
      if (success) {
        setUserConnection(null);
        toast({
          title: "Disconnected",
          description: "Your Telegram account has been disconnected from the Payment Bot."
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to disconnect.",
        variant: "destructive"
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const openTelegram = () => {
    if (botUsername) {
      window.open(`https://t.me/${botUsername}`, '_blank');
    }
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!isConfigured) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Bot
            </DialogTitle>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertDescription>
              The Payment Bot is not configured. Please contact your administrator.
            </AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Bot Connection
          </DialogTitle>
          <DialogDescription>
            Connect your Telegram to receive payment request notifications and approve them directly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {userConnection ? (
            // Connected state
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <div className="flex-1">
                  <p className="font-medium">Connected</p>
                  <p className="text-sm text-muted-foreground">
                    {userConnection.telegram_username 
                      ? `@${userConnection.telegram_username}` 
                      : 'Telegram account linked'}
                  </p>
                </div>
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
                  Active
                </Badge>
              </div>

              <p className="text-sm text-muted-foreground">
                You'll receive notifications for payment requests that need your attention.
              </p>

              <Button
                variant="outline"
                className="w-full"
                onClick={handleDisconnect}
                disabled={isDisconnecting}
              >
                {isDisconnecting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Unlink className="mr-2 h-4 w-4" />
                )}
                Disconnect
              </Button>
            </div>
          ) : (
            // Not connected state
            <div className="space-y-4">
              {connectionCode ? (
                <>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Your connection command:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-3 bg-muted rounded-md font-mono text-sm">
                        /connect {connectionCode}
                      </code>
                      <Button size="icon" variant="outline" onClick={handleCopyCode}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    {expiresAt && (
                      <p className="text-xs text-muted-foreground">
                        Expires: {new Date(expiresAt).toLocaleString()}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    <p className="font-medium">Steps to connect:</p>
                    <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                      <li>Open the Payment Bot in Telegram</li>
                      <li>Send the command above</li>
                      <li>You'll receive a confirmation message</li>
                    </ol>
                  </div>

                  <div className="flex gap-2">
                    {botUsername && (
                      <Button variant="default" className="flex-1" onClick={openTelegram}>
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Open Bot
                      </Button>
                    )}
                    <Button variant="outline" onClick={handleGenerateCode} disabled={isGeneratingCode}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      New Code
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Generate a connection code to link your Telegram account. This will allow you to:
                  </p>
                  <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                    <li>Receive payment request notifications</li>
                    <li>Approve or reject requests via Telegram</li>
                    <li>Confirm payments directly</li>
                  </ul>

                  <Button 
                    className="w-full" 
                    onClick={handleGenerateCode}
                    disabled={isGeneratingCode}
                  >
                    {isGeneratingCode ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="mr-2 h-4 w-4" />
                    )}
                    Generate Connection Code
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
